import { useEffect, useState } from "react";
import {
  Input,
  Button,
  Card,
  useToast,
  EmptyState,
  PageHeader,
  FormField,
  PageLayout,
  Toolbar,
  Pagination,
  useModal,
  Tooltip,
  Switch,
  Select,
} from "@dawa/ui";
import { Link } from "react-router-dom";
import { Briefcase, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/api";

interface Project {
  id: string;
  name: string;
  domain?: string;
  gsc_property?: string;
  sitemap_url?: string;
  created_at?: string;
}

export default function Projects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const { addToast, Toasts } = useToast();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 9;
  const [pagesCount, setPagesCount] = useState<Record<string, number>>({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    domain: "",
    gsc_property: "",
    sitemap_url: "",
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Project>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const {
    Modal: DeleteModal,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal();
  const [toDelete, setToDelete] = useState<Project | null>(null);
  const [withSitemapOnly, setWithSitemapOnly] = useState(false);
  const [importingAll, setImportingAll] = useState(false);
  const [sortBy, setSortBy] = useState<"date_desc" | "name_asc" | "name_desc">("date_desc");
  const [updateErrors, setUpdateErrors] = useState<Record<string, string | null>>({});
  const filterStorageKey = "projects.filters.v1";
  const hasActiveFilters = Boolean(q.trim()) || withSitemapOnly || sortBy !== "date_desc";
  const stats = {
    total: projects.length,
    withSitemap: projects.filter((p) => Boolean(p.sitemap_url)).length,
    withGsc: projects.filter((p) => Boolean(p.gsc_property)).length,
    withDomain: projects.filter((p) => Boolean(p.domain)).length,
  };

  async function load() {
    const s = await supabase.auth.getSession();
    const token = s.data.session?.access_token;
    if (!token) return;
    const res = await apiFetch("/v1/projects", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) addToast("Échec du chargement des projets", "error");
    const data = await res.json().catch(() => []);
    if (!Array.isArray(data)) {
      addToast("Format inattendu pour la liste des projets", "error");
      setProjects([]);
    } else {
      setProjects(data);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(filterStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        q?: string;
        withSitemapOnly?: boolean;
        sortBy?: "date_desc" | "name_asc" | "name_desc";
      };
      if (typeof parsed.q === "string") setQ(parsed.q);
      if (typeof parsed.withSitemapOnly === "boolean") setWithSitemapOnly(parsed.withSitemapOnly);
      if (parsed.sortBy) setSortBy(parsed.sortBy);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(filterStorageKey, JSON.stringify({ q, withSitemapOnly, sortBy }));
    } catch {
      /* ignore */
    }
  }, [filterStorageKey, q, sortBy, withSitemapOnly]);

  useEffect(() => {
    if (showForm) {
      setFormError(null);
    }
  }, [showForm]);

  useEffect(() => {
    (async () => {
      try {
        const s = await supabase.auth.getSession();
        const token = s.data.session?.access_token;
        if (!token) return;
        const list = projects.slice(0, 10);
        for (const p of list) {
          const res = await apiFetch(`/v1/projects/${p.id}/pages`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const arr = await res.json().catch(() => []);
          if (Array.isArray(arr)) setPagesCount((m) => ({ ...m, [p.id]: arr.length }));
        }
      } catch {
        /* ignore */
      }
    })();
  }, [projects]);

  async function addProject() {
    const s = await supabase.auth.getSession();
    const token = s.data.session?.access_token;
    if (!token) return;
    setCreating(true);
    setFormError(null);
    try {
      const res = await apiFetch("/v1/projects", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setForm({ name: "", domain: "", gsc_property: "", sitemap_url: "" });
        setShowForm(false);
        addToast("Projet créé", "success");
        await load();
      } else {
        const body = await res.json().catch(() => ({}));
        const message = body?.error || `Erreur ${res.status}`;
        setFormError(message);
        addToast("Impossible de créer le projet", "error");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erreur réseau";
      setFormError(message);
      addToast(message, "error");
    } finally {
      setCreating(false);
    }
  }

  const filteredBase = q
    ? projects.filter((p) =>
        [p.name, p.domain, p.gsc_property].some((v) =>
          String(v || "")
            .toLowerCase()
            .includes(q.toLowerCase()),
        ),
      )
    : projects;
  const filtered = withSitemapOnly ? filteredBase.filter((p) => p.sitemap_url) : filteredBase;
  const sorted = filtered.slice().sort((a, b) => {
    if (sortBy === "name_asc") return (a.name || "").localeCompare(b.name || "");
    if (sortBy === "name_desc") return (b.name || "").localeCompare(a.name || "");
    const da = a.created_at ? new Date(a.created_at).getTime() : 0;
    const db = b.created_at ? new Date(b.created_at).getTime() : 0;
    return db - da;
  });
  const total = sorted.length;
  const slice = sorted.slice((page - 1) * pageSize, page * pageSize);
  const eligibleSitemaps = projects.filter((p) => p.sitemap_url);

  return (
    <PageLayout maxWidth="full" className="space-y-6">
      <PageHeader
        title="Projets"
        breadcrumbs={[{ label: "Dashboard", href: "/dashboard" }, { label: "Projets" }]}
        actions={
          <Button className="flex items-center gap-2" onClick={() => setShowForm((s) => !s)}>
            <Plus className="h-4 w-4" /> Ajouter
          </Button>
        }
      />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="p-4">
          <p className="text-xs uppercase text-muted">Total projets</p>
          <p className="mt-2 text-2xl font-semibold">{stats.total}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase text-muted">Avec domaine</p>
          <p className="mt-2 text-2xl font-semibold">{stats.withDomain}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase text-muted">Avec GSC</p>
          <p className="mt-2 text-2xl font-semibold">{stats.withGsc}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase text-muted">Avec sitemap</p>
          <p className="mt-2 text-2xl font-semibold">{stats.withSitemap}</p>
        </Card>
      </div>
      <Toolbar
        searchValue={q}
        onSearchChange={(v) => {
          setQ(v);
          setPage(1);
        }}
        placeholder="Rechercher un projet"
        actions={
          <div className="inline-flex items-center gap-2 text-sm">
            <span className="text-muted" aria-live="polite">
              {total} projet{total > 1 ? "s" : ""}
            </span>
            <Select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as any);
                setPage(1);
              }}
            >
              <option value="date_desc">Tri: plus récent</option>
              <option value="name_asc">Nom A→Z</option>
              <option value="name_desc">Nom Z→A</option>
            </Select>
            <div className="mx-2 h-4 w-px bg-muted/40" />
            <Switch
              checked={withSitemapOnly}
              onCheckedChange={(v) => {
                setWithSitemapOnly(v);
                setPage(1);
              }}
              aria-label="Avec sitemap seulement"
            />
            <span>Avec sitemap</span>
            <div className="mx-2 h-4 w-px bg-muted/40" />
            {hasActiveFilters && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setQ("");
                  setWithSitemapOnly(false);
                  setSortBy("date_desc");
                  setPage(1);
                }}
              >
                Réinitialiser filtres
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              disabled={importingAll || eligibleSitemaps.length === 0}
              onClick={async () => {
                setImportingAll(true);
                try {
                  const s = await supabase.auth.getSession();
                  const token = s.data.session?.access_token;
                  if (!token) return;
                  let ok = 0,
                    fail = 0;
                  for (const p of eligibleSitemaps) {
                    try {
                      const r = await apiFetch(`/v1/projects/${p.id}/sitemap/import`, {
                        method: "POST",
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (r.ok) ok++;
                      else fail++;
                    } catch {
                      fail++;
                    }
                  }
                  addToast(
                    `Import sitemaps: ${ok} ok, ${fail} erreurs`,
                    ok > 0 && fail === 0 ? "success" : "info",
                  );
                  await load();
                } finally {
                  setImportingAll(false);
                }
              }}
            >
              {importingAll ? "Import en cours..." : "Importer tous les sitemaps"}
            </Button>
          </div>
        }
      />

      {showForm && (
        <Card className="p-4">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void addProject();
            }}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold">Nouveau projet</h3>
                <p className="text-sm text-muted">
                  Ajoutez un site ou domaine pour centraliser vos contenus.
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowForm(false);
                  setFormError(null);
                }}
              >
                Fermer
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Nom" htmlFor="project-name" required>
                <Input
                  id="project-name"
                  placeholder="Nom du projet"
                  value={form.name}
                  autoFocus
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </FormField>
              <FormField label="Domaine" htmlFor="project-domain" help="Ex: example.com">
                <Input
                  id="project-domain"
                  placeholder="example.com"
                  value={form.domain}
                  onChange={(e) => setForm({ ...form, domain: e.target.value })}
                />
              </FormField>
              <FormField label="Propriété GSC" htmlFor="project-gsc" help="Google Search Console">
                <Input
                  id="project-gsc"
                  placeholder="https://search.google.com/..."
                  value={form.gsc_property}
                  onChange={(e) => setForm({ ...form, gsc_property: e.target.value })}
                />
              </FormField>
              <FormField label="URL du sitemap" htmlFor="project-sitemap">
                <Input
                  id="project-sitemap"
                  placeholder="https://example.com/sitemap.xml"
                  value={form.sitemap_url}
                  onChange={(e) => setForm({ ...form, sitemap_url: e.target.value })}
                />
              </FormField>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" disabled={!form.name.trim()} loading={creating}>
                Ajouter
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setForm({ name: "", domain: "", gsc_property: "", sitemap_url: "" });
                  setFormError(null);
                }}
              >
                Réinitialiser
              </Button>
            </div>
            {formError && <p className="text-sm text-danger">{formError}</p>}
          </form>
        </Card>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          title={projects.length === 0 ? "Aucun projet" : "Aucun résultat"}
          description={
            projects.length === 0
              ? "Créez un projet pour regrouper vos contenus par site ou domaine."
              : "Aucun projet ne correspond à votre recherche ou aux filtres actifs."
          }
          action={
            <div className="flex flex-wrap items-center gap-2">
              {projects.length > 0 && hasActiveFilters && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setQ("");
                    setWithSitemapOnly(false);
                    setSortBy("date_desc");
                    setPage(1);
                  }}
                >
                  Réinitialiser les filtres
                </Button>
              )}
              <Button onClick={() => setShowForm(true)}>Créer un projet</Button>
            </div>
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {slice.map((p) => (
            <motion.div key={p.id} whileHover={{ scale: 1.02 }}>
              <Card className="flex h-full flex-col justify-between p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 text-accent overflow-hidden">
                    {p.domain ? (
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(p.domain)}&sz=64`}
                        alt="favicon"
                        className="h-12 w-12 object-contain"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <Briefcase className="h-6 w-6" />
                    )}
                  </div>
                  <div className="flex-1 space-y-1">
                    {editing === p.id ? (
                      <>
                        <Input
                          aria-label="Nom du projet"
                          placeholder="Nom du projet"
                          value={drafts[p.id]?.name ?? p.name}
                          onChange={(e) =>
                            setDrafts((d) => ({
                              ...d,
                              [p.id]: { ...(d[p.id] || p), name: e.target.value },
                            }))
                          }
                        />
                        <Input
                          aria-label="Domaine du projet"
                          placeholder="example.com"
                          value={drafts[p.id]?.domain ?? p.domain ?? ""}
                          onChange={(e) =>
                            setDrafts((d) => ({
                              ...d,
                              [p.id]: { ...(d[p.id] || p), domain: e.target.value },
                            }))
                          }
                        />
                        <Input
                          aria-label="Propriété GSC"
                          placeholder="https://search.google.com/..."
                          value={drafts[p.id]?.gsc_property ?? p.gsc_property ?? ""}
                          onChange={(e) =>
                            setDrafts((d) => ({
                              ...d,
                              [p.id]: { ...(d[p.id] || p), gsc_property: e.target.value },
                            }))
                          }
                        />
                        <Input
                          aria-label="URL du sitemap"
                          placeholder="https://example.com/sitemap.xml"
                          value={drafts[p.id]?.sitemap_url ?? p.sitemap_url ?? ""}
                          onChange={(e) =>
                            setDrafts((d) => ({
                              ...d,
                              [p.id]: { ...(d[p.id] || p), sitemap_url: e.target.value },
                            }))
                          }
                        />
                      </>
                    ) : (
                      <>
                        <h3 className="font-semibold">{p.name}</h3>
                        <p className="text-sm text-muted">{p.domain}</p>
                        {p.gsc_property && (
                          <p className="text-sm text-muted">GSC: {p.gsc_property}</p>
                        )}
                        {p.sitemap_url && (
                          <p className="text-sm text-muted">Sitemap: {p.sitemap_url}</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {pagesCount && pagesCount[p.id] !== undefined && (
                    <span className="mr-auto text-xs text-muted">
                      Pages importées: {pagesCount[p.id]}
                    </span>
                  )}
                  {p.sitemap_url && (
                    <Tooltip label="Importer/rafraîchir les URLs du sitemap">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={async () => {
                          const s = await supabase.auth.getSession();
                          const token = s.data.session?.access_token;
                          if (!token) return;
                          const res = await apiFetch(`/v1/projects/${p.id}/sitemap/import`, {
                            method: "POST",
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          if (res.ok) {
                            addToast("Sitemap importé", "success");
                            await load();
                          } else {
                            addToast("Échec de l'import du sitemap", "error");
                          }
                        }}
                      >
                        Importer sitemap
                      </Button>
                    </Tooltip>
                  )}
                  {p.sitemap_url && (
                    <Tooltip label="Tester le sitemap (sans import)">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          const s = await supabase.auth.getSession();
                          const token = s.data.session?.access_token;
                          if (!token) return;
                          const res = await apiFetch(`/v1/projects/${p.id}/sitemap/test`, {
                            method: "GET",
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          if (res.ok) {
                            const j = await res.json().catch(() => ({}));
                            const cnt = typeof j.count === "number" ? j.count : 0;
                            addToast(`Sitemap OK – ${cnt} URL(s) trouvée(s)`, "success");
                          } else {
                            addToast("Test sitemap: erreur", "error");
                          }
                        }}
                      >
                        Tester
                      </Button>
                    </Tooltip>
                  )}
                  {editing === p.id ? (
                    <>
                      <Button
                        size="sm"
                        onClick={async () => {
                          setSavingId(p.id);
                          const s = await supabase.auth.getSession();
                          const token = s.data.session?.access_token;
                          if (!token) return;
                          const { id, ...payload } = drafts[p.id] || p;
                          setUpdateErrors((prev) => ({ ...prev, [p.id]: null }));
                          try {
                            const res = await apiFetch(`/v1/projects/${id}`, {
                              method: "PATCH",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                              },
                              body: JSON.stringify(payload),
                            });
                            if (res.ok) {
                              setEditing(null);
                              setDrafts((d) => {
                                const nextDrafts = { ...d };
                                delete nextDrafts[p.id];
                                return nextDrafts;
                              });
                              setUpdateErrors((prev) => {
                                if (prev[p.id] === undefined) return prev;
                                const next = { ...prev };
                                delete next[p.id];
                                return next;
                              });
                              addToast("Projet mis à jour", "success");
                              await load();
                            } else {
                              const body = await res.json().catch(() => ({}));
                              const message = body?.error || `Erreur ${res.status}`;
                              setUpdateErrors((prev) => ({ ...prev, [p.id]: message }));
                              addToast("Échec de la mise à jour", "error");
                            }
                          } catch (err) {
                            const message = err instanceof Error ? err.message : "Erreur réseau";
                            setUpdateErrors((prev) => ({ ...prev, [p.id]: message }));
                            addToast(message, "error");
                          } finally {
                            setSavingId(null);
                          }
                        }}
                        disabled={savingId === p.id}
                      >
                        {savingId === p.id ? "Enregistrement..." : "Enregistrer"}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setEditing(null);
                          setDrafts((d) => {
                            const nextDrafts = { ...d };
                            delete nextDrafts[p.id];
                            return nextDrafts;
                          });
                          setUpdateErrors((prev) => {
                            if (prev[p.id] === undefined) return prev;
                            const next = { ...prev };
                            delete next[p.id];
                            return next;
                          });
                        }}
                      >
                        Annuler
                      </Button>
                      {updateErrors[p.id] && (
                        <p className="text-sm text-danger">{updateErrors[p.id]}</p>
                      )}
                    </>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => {
                        setDrafts((d) => ({ ...d, [p.id]: p }));
                        setUpdateErrors((prev) => ({ ...prev, [p.id]: null }));
                        setEditing(p.id);
                      }}
                    >
                      Éditer
                    </Button>
                  )}
                  <Link to={`/projects/${p.id}`} className="text-sm underline ml-auto">
                    Détails
                  </Link>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      setToDelete(p);
                      openDeleteModal();
                    }}
                    disabled={deletingId === p.id}
                  >
                    {deletingId === p.id ? "Suppression..." : "Supprimer"}
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
      {filtered.length > 0 && (
        <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} />
      )}
      <DeleteModal>
        {toDelete && (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Supprimer le projet</h3>
            <p className="text-sm text-muted">
              Cette action est irréversible. Confirmez la suppression de « {toDelete.name} ».
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setToDelete(null);
                  closeDeleteModal();
                }}
              >
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  if (!toDelete) return;
                  setDeletingId(toDelete.id);
                  const s = await supabase.auth.getSession();
                  const token = s.data.session?.access_token;
                  if (!token) return;
                  try {
                    const res = await apiFetch(`/v1/projects/${toDelete.id}`, {
                      method: "DELETE",
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (res.ok) {
                      addToast("Projet supprimé", "success");
                    } else {
                      const body = await res.json().catch(() => ({}));
                      addToast(body?.error || "Échec de la suppression", "error");
                    }
                  } catch (err) {
                    addToast(err instanceof Error ? err.message : "Erreur réseau", "error");
                  } finally {
                    setDeletingId(null);
                    setToDelete(null);
                    closeDeleteModal();
                    await load();
                  }
                }}
              >
                Confirmer
              </Button>
            </div>
          </div>
        )}
      </DeleteModal>
      <Toasts />
    </PageLayout>
  );
}
